import { useEffect, useState } from 'react'
import { isAndroid, isIOS } from 'react-device-detect'

import { isCurrentAppVersionAtLeast } from '@dhl/native-bridge'
import { consentGroups, getConsentedGroups, oneTrustConsentChangedEvent } from '@dhl/onetrust'
import { isNative } from '@dhl/webview'

import { type ThirdPartyConfig } from './ThirdParty.js'

const minAppVersionRequired = '5.6.0'

export type TrackingState = {
  isOneTrustEnabled: boolean
  isOneTrustEnabledInNative: boolean
  hasAnalyticsConsent: boolean
  showTrackingScreen: boolean
}

export const useTrackingState = (config: ThirdPartyConfig, isNativeTrackingAllowed?: boolean): TrackingState => {
  const { oneTrustId, oneTrustNativeEnabled, oneTrustNativeEnabledFromVersion } = config

  const [, setConsentVersion] = useState(0)

  useEffect(() => {
    if (!oneTrustId) {
      return undefined
    }

    const onConsentChanged = () => setConsentVersion((v) => v + 1)
    document.addEventListener(oneTrustConsentChangedEvent, onConsentChanged)
    return () => document.removeEventListener(oneTrustConsentChangedEvent, onConsentChanged)
  }, [oneTrustId])

  const nativeOneTrustAllowedForVersion =
    isNative() && !!oneTrustNativeEnabled && isCurrentAppVersionAtLeast(oneTrustNativeEnabledFromVersion ?? '')

  const isOneTrustEnabledInBrowser = !isNative() && !!oneTrustId
  const isOneTrustEnabledInNative =
    !!oneTrustId &&
    ((nativeOneTrustAllowedForVersion && isAndroid) ||
      (nativeOneTrustAllowedForVersion && isIOS && !!isNativeTrackingAllowed))

  const isOneTrustEnabled = isOneTrustEnabledInBrowser || isOneTrustEnabledInNative
  const hasAnalyticsConsent = isOneTrustEnabled && getConsentedGroups().includes(consentGroups.analytics)

  return {
    isOneTrustEnabled,
    isOneTrustEnabledInNative,
    hasAnalyticsConsent,
    showTrackingScreen:
      isCurrentAppVersionAtLeast(minAppVersionRequired) && isNativeTrackingAllowed === undefined && isIOS,
  }
}
